/**
 * Custom auth redirect HOC (replacement for redux-auth-wrapper).
 * Wraps a component and redirects when authenticatedSelector(state) is false.
 */

import React from 'react'
import { connect } from 'react-redux'
import { normalizePathname } from 'utils/navigation'

const AUTH_REDIRECT_SESSION_KEY = 'adminpanel.authRedirect.last'

function shouldSkipAuthRedirect(displayName, redirectPath) {
  if (typeof window === 'undefined' || redirectPath == null) return false

  const currentPathname = normalizePathname(window.location.pathname)
  const targetPathname = normalizePathname(redirectPath)
  if (currentPathname === targetPathname) {
    return true
  }

  try {
    const raw = window.sessionStorage.getItem(AUTH_REDIRECT_SESSION_KEY)
    if (!raw) return false
    const parsed = JSON.parse(raw)
    const sameTarget =
      parsed?.targetPathname === targetPathname && parsed?.wrapper === displayName
    return sameTarget && Date.now() - (parsed?.at || 0) < 5000
  } catch {
    return false
  }
}

function rememberAuthRedirect(displayName, redirectPath) {
  if (typeof window === 'undefined' || redirectPath == null) return
  try {
    window.sessionStorage.setItem(
      AUTH_REDIRECT_SESSION_KEY,
      JSON.stringify({
        wrapper: displayName,
        targetPathname: normalizePathname(redirectPath),
        at: Date.now(),
      }),
    )
  } catch {
    // ignore storage errors (private mode, quota, etc.)
  }
}

/**
 * Creates an HOC that redirects to redirectPath when authenticatedSelector returns false.
 * @param {Object} options
 * @param {function(state): boolean} options.authenticatedSelector
 * @param {string|function(state, ownProps): string} options.redirectPath
 * @param {function(path): (dispatch?) => void} options.redirectAction - receives path; may return a thunk
 * @param {string} [options.wrapperDisplayName] - for React DevTools
 * @returns {function(Component): Component}
 */
export function createAuthRedirect(options) {
  const {
    authenticatedSelector,
    redirectPath,
    redirectAction,
    wrapperDisplayName = 'AuthRedirect',
  } = options

  return function withAuthRedirect(WrappedComponent) {
    const mapStateToProps = (state, ownProps) => {
      try {
        const authenticated = authenticatedSelector(state)
        const path =
          typeof redirectPath === 'function' ? redirectPath(state, ownProps) : redirectPath
        return { __authenticated: authenticated, __redirectPath: path }
      } catch (err) {
        if (typeof __DEV__ !== 'undefined' && __DEV__) {
          console.error(`[${wrapperDisplayName}] auth selector threw`, err)
        }
        return {
          __authenticated: false,
          __redirectPath: typeof redirectPath === 'string' ? redirectPath : '/login',
        }
      }
    }

    function AuthRedirectWrapper(props) {
      const { __authenticated, __redirectPath, dispatch, ...rest } = props
      const didRedirect = React.useRef(false)

      React.useEffect(() => {
        if (didRedirect.current || __authenticated) return
        if (__redirectPath == null) return
        if (shouldSkipAuthRedirect(wrapperDisplayName, __redirectPath)) return
        didRedirect.current = true
        rememberAuthRedirect(wrapperDisplayName, __redirectPath)
        const action = redirectAction(__redirectPath)
        if (typeof action === 'function') {
          dispatch(action)
        } else {
          action()
        }
      }, [__authenticated, __redirectPath, dispatch])

      if (!__authenticated) return null
      return <WrappedComponent {...rest} />
    }

    AuthRedirectWrapper.displayName = `${wrapperDisplayName}(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`

    return connect(mapStateToProps)(AuthRedirectWrapper)
  }
}
